import { Button, Card, Row, Col, List } from 'antd';
import { ButtonProps } from "antd/lib/button";
import * as React from 'react';

import { ProductDataService } from 'src/app/products/products/form/product-form.data';
import { MapMarker, MapView } from 'src/core/ui/map.view';
import { AccommodationSummaryView } from 'src/app/bookings/search/results/search-results-accommodation-summary.view';
import { RoomSummaryView } from 'src/app/bookings/search/results/search-results-room-summary.view';
import { ProductCategoryListDataService } from 'src/app/products/product-categories/list/product-category-list.data';
import { ContextService } from 'src/core/services/context.service';

type ViewType = "supplements" | "hotel" | "map" | "room";

interface AccommodationInfoCardProps {
	productId: number;
	setBusy: (isBusy: boolean) => void;
	modifications: any;

}

interface AccommodationInfoCardState {
	loadedProduct?: ProductItem;
	parentProduct?: ProductItem;
	activeView: ViewType;
	nearPlaces?: { name: string; distance: string; }[];
}

export class AccommodationInfoCard extends React.Component<AccommodationInfoCardProps, AccommodationInfoCardState>{
	productDataService: ProductDataService;
	categoryDataService: ProductCategoryListDataService;
	contextService: ContextService;

	constructor(props: Readonly<AccommodationInfoCardProps>) {
		super(props);
		const { modifications } = this.props;

		this.productDataService = ProductDataService.make();
		this.categoryDataService = ProductCategoryListDataService.make(this.contextService);

		this.state = {
			activeView: modifications ? 'supplements' : "room",
		};
	}

	componentDidMount() {
		this.loadProduct().then(() => this.loadParent(this.state.loadedProduct));
	}

	async loadProduct() {
		if (this.props.productId) {
			this.productDataService.addFilter("id", "eq", this.props.productId);
			this.productDataService.addSorting("id", "asc");
			this.productDataService.pageSize = 1;
			await this.productDataService.load(query => query);

			const product = this.productDataService.item;

			if (product) {
				this.categoryDataService.addFilter('id', 'eq', product.categoryId);
				this.categoryDataService.addSorting('id', 'asc', true);
				this.categoryDataService.pageSize = 1;
				await this.categoryDataService.load(query => query);

				product.category = this.categoryDataService.item;

				this.setState({
					loadedProduct: product,
				});
			}
		}
	}
	async loadParent(product: ProductItem) {
		if (product && product.parentProductId) {
			this.productDataService.addFilter("id", "eq", product.parentProductId);
			this.productDataService.addSorting("id", "asc");
			this.productDataService.pageSize = 1;
			await this.productDataService.load(query => query);

			const parent = this.productDataService.item;
			if (parent) {
				this.categoryDataService.addFilter('id', 'eq', parent.categoryId);
				this.categoryDataService.addSorting('id', 'asc', true);
				this.categoryDataService.pageSize = 1;
				await this.categoryDataService.load(query => query);

				parent.category = this.categoryDataService.item;

				this.setState({
					parentProduct: parent,
				});
			}
		}
	}

	private renderMap(location) {
		const point = location ? { lat: location.latitude, lng: location.longitude } : undefined;
		return (
			<Row type={"flex"} justify={"space-between"}>
				<Col span={7}>
					<div style={{ width: '100%', height: '50vh' }}>
						<List
							className="place-list"
							itemLayout="vertical"
							size="small"
							header={<h3>Closest landmarks</h3>}
							bordered
							dataSource={this.state.nearPlaces || []}
							renderItem={item => (
								<List.Item extra={item.distance} key={item.name}>{item.name}</List.Item>
							)}
						/>
					</div>
				</Col>
				<Col span={16}>
					<div style={{ width: '100%', height: '50vh' }}>
						<MapView
							center={point}
							zoom={13}
							initMap={this.searchPlaces}
						>
							<MapMarker {...point} />
						</MapView>
					</div>
				</Col>
			</Row>
		);
	}

	handleChangeView(activeView: ViewType) {
		this.setState({
			activeView: activeView
		})
	}

	private searchPlaces = (map: google.maps.Map, maps, places: google.maps.LatLngLiteral): void => {
		const placesService = new google.maps.places.PlacesService(map);
		placesService.nearbySearch({
			location: places,
			radius: 6000,
			//types: ['restaurant']
		}, this.placeSearchResult);
	};

	private placeSearchResult = (
		results: google.maps.places.PlaceResult[],
		status: google.maps.places.PlacesServiceStatus,
		pagination: google.maps.places.PlaceSearchPagination
	) => {
		const product = this.state.parentProduct;
		if (status == google.maps.places.PlacesServiceStatus.OK && product && product.location) {
			const location: any = product.location;
			const point = new google.maps.LatLng(location.latitude, location.longitude);
			const places = results.map(p => ({
				name: p.name,
				distance: `${(google.maps.geometry.spherical.computeDistanceBetween(p.geometry.location, point) / 1000).toFixed(2)} km`,
			}));
			this.setState({ nearPlaces: places });
		}
	}

	private getButtonPropsForActiveView(viewName: ViewType): ButtonProps {
		const { activeView, loadedProduct, parentProduct } = this.state;
		const { modifications } = this.props;

		return {
			type: activeView === viewName ? 'primary' : undefined,
			disabled: (viewName === "room" && loadedProduct == null)
				|| (viewName === "map" || viewName === "hotel") && parentProduct == null
				|| (viewName === "map" && parentProduct.location == null)
				|| (viewName === "supplements" && !modifications)
				|| false
		};
	}

	public render(): React.ReactNode {
		const { activeView, loadedProduct, parentProduct } = this.state;
		const { setBusy, modifications } = this.props;

		const actions =
			[
				<Button
					key="supplements"
					icon="search"
					onClick={() => this.handleChangeView('supplements')}
					{...this.getButtonPropsForActiveView('supplements')}
				>
					Supplement/Discounts
				</Button>,
				<Button
					key="room"
					icon="shopping-cart"
					onClick={() => this.handleChangeView('room')}
					{...this.getButtonPropsForActiveView('room')}
				>
					Room Info
				</Button>,
				<Button
					key="map"
					icon="environment"
					onClick={() => this.handleChangeView('map')}
					{...this.getButtonPropsForActiveView('map')}
				>
					Map
				</Button>,
				<Button
					key="hotel"
					icon="shopping-cart"
					onClick={() => this.handleChangeView('hotel')}
					{...this.getButtonPropsForActiveView('hotel')}
				>
					Hotel Info
				</Button>
			];

		const roomInfo = (
			<RoomSummaryView
				product={loadedProduct}
				setBusy={setBusy}
				horizontal={true}
				picturesWidth={500}
				textWidth={300}
				externalPadding={10}
				internalPadding={5}
				colSpan={10}
			/>
		);

		const hotelInfo = (
			<AccommodationSummaryView
				product={parentProduct}
				setBusy={setBusy}
				left={true}
				picturesWidth={400}
				textWidth={400}
				externalPadding={10}
				internalPadding={5}
				colSpan={3}
			/>
		);

		return (
			<Card
				title={"PRODUCTS/SUPPLEMENTS/DISCOUNTS"}
				// extra={extra}  aki va el total
				actions={actions}
			>
				{loadedProduct == null && this.props.productId == null && "NO PRODUCT MAPPING SET"}
				{activeView === 'supplements' && modifications}
				{activeView === 'room' && roomInfo}
				{activeView === 'map' && this.renderMap(parentProduct.location)}
				{activeView === 'hotel' && hotelInfo}
			</Card>
		);
	}
};



// WEBPACK FOOTER //
// ./src/app/bookings/quote-entries/parts/accommodation-info-card.tsx